import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import {Helmet} from "react-helmet"
import {useDispatch} from "react-redux/es/hooks/useDispatch"
import {useSelector} from "react-redux/es/hooks/useSelector"
import {getPageDetails} from "../../Redux/PageDetailsReducer"
import {
  getCoursesDetails,
  getPostsDetails,
  setMainIsLoading,
} from "../../Redux/PostsDetailsReducer"
import Title, {TitleHeader} from "../../sharedComponents/Title"
import {openLink} from "../../utils"
import {NavigationSection} from "../Sections/NavigationSection"
import DeadlinesListSection from "../Sections/DeadlinesListSection"
import moment from "moment"
import {MainLoader} from "../../sharedComponents/Loader/Loader"
import {useMediaQuery} from "react-responsive"
import {InView} from "react-intersection-observer"

const Newsletter = lazy(() =>
  import("../../sharedComponents/NewsLetter/Newsletter")
)
const CarouselListSection = lazy(() =>
  import("../Sections/CarouselListSection")
)
const DeadlinesScroller = lazy(() =>
  import("../../sharedComponents/DeadlinesScroller/DeadlinesScroller")
)
const Banner = lazy(() => import("../../sharedComponents/Banner/Banner"))
const PostList = lazy(() => import("./PostList"))
const EventList = lazy(() => import("./EventList"))
const SkeletonLoader = lazy(() =>
  import("../../sharedComponents/SkeletonLoader/SkeletonLoader")
)
const Scroller = lazy(() => import("../../sharedComponents/Scroller/Scroller"))

const PageDetails = ({id, pageName, pageSections = []}) => {
  const dispatch = useDispatch()
  const data = useSelector((state) => state.pageDetails.pages)
  const isMainLoading = useSelector((state) => state.postsDetails.isMainLoading)
  const postsData = useSelector((state) => state.postsDetails)
  const campaigns = useSelector((state) => state.auth.campaignDetails)
  const [sections, setSections] = useState([])
  const [sidebars, setsidebars] = useState([])
  const isSmallScreen = useMediaQuery({maxWidth: 991})

  const memoizedCampaigns = useMemo(() => campaigns, [campaigns])

  useEffect(() => {
    if (!Object.keys(data).includes(pageName)) dispatch(getPageDetails(id))
    //eslint-disable-next-line
  }, [id])

  useEffect(() => {
    setsidebars(data?.[pageName]?.sidebars)
    //eslint-disable-next-line
  }, [data?.[pageName]?.sidebars, pageName])
  useEffect(() => {
    setSections(data?.[pageName]?.sections)
    //eslint-disable-next-line
  }, [data?.[pageName]?.sections, pageName])

  //add type for every new component to find last section id for main loading

  const getLastSectionID = useCallback(
    (sections = []) => {
      let id = -1
      pageSections.forEach((section) => {
        if (
          section.type === "event-list" ||
          section.type === "event-carousel" ||
          section.type === "post-list" ||
          section.type === "post-carousel" ||
          section.type === "post-scroller" ||
          section.type === "event-scroller" ||
          section.type === "course-scroller" ||
          section.type === "course-list" ||
          section.type === "course-carousel"
        ) {
          id = section.id
        }
      })

      return id
    },
    [pageSections]
  )

  const lastSectionId = useMemo(() => getLastSectionID(), [getLastSectionID])

  useEffect(() => {
    if (
      data?.[pageName]?.sections &&
      data?.[pageName]?.sections?.length > 0 &&
      getLastSectionID(data?.[pageName]?.sections) !== -1
    ) {
      dispatch(setMainIsLoading(true))

      data?.[pageName]?.sections.forEach((ele) => {
        if (
          ele.type === "course-carousel" ||
          ele.type === "course-list" ||
          ele.type === "course-scroller"
        ) {
          const paramsObject = {
            offset: ele.offset,
            option_for_featured: ele.option_for_featured,
            total_items: ele.total_items,
            pagination: ele.pagination,
            items_per_page: ele.items_per_page,
            sorting: parseInt(ele.sorting),
            groups: ele.groups,
            groups_condition: ele.groups_condition,
            categories: ele.categories,
            categories_condition: ele.categories_condition,
            tags: ele.tags,
            tags_condition: ele.tags_condition,
            rule_type: ele.rule_type,
            type: ele.type,
            disable_latest_record: ele.disable_latest_record,
          }
          dispatch(
            getCoursesDetails({
              paramsObject,
              id: ele.id,
              pageName,
              type: ele.type,
              title: ele.title,
              sectionId: ele.section_id,
              isLast: ele.id === lastSectionId,
            })
          )
        }

        if (ele.type === "post-carousel" || ele.type === "event-carousel") {
          const paramsObject = {
            offset: ele.offset,
            option_for_featured: ele.option_for_featured,
            total_items: ele.total_items,
            sorting: parseInt(ele.sorting),
            groups: ele.groups,
            groups_condition: ele.groups_condition,
            categories: ele.categories,
            categories_condition: ele.categories_condition,
            tags: ele.tags,
            tags_condition: ele.tags_condition,
            rule_type: ele.rule_type,
            type: ele.type,
            disable_latest_record: ele.disable_latest_record,
          }
          dispatch(
            getPostsDetails({
              paramsObject,
              id: ele.id,
              pageName,
              type: ele.type,
              title: ele.title,
              sectionId: ele.section_id,
              isLast: ele.id === lastSectionId,
            })
          )
        } else if (
          ele.type === "post-scroller" ||
          ele.type === "event-scroller"
        ) {
          const paramsObject = {
            offset: ele.offset,
            option_for_featured: ele.option_for_featured,
            total_items: ele.maximum_items,
            sorting: parseInt(ele.sorting),
            groups: ele.groups,
            groups_condition: ele.groups_condition,
            categories: ele.categories,
            categories_condition: ele.categories_condition,
            tags: ele.tags,
            tags_condition: ele.tags_condition,
            rule_type: ele.rule_type,
            disable_latest_record: ele.disable_latest_record,
            type:
              ele.type === "post-scroller" ? "post-carousel" : "event-carousel",
          }
          dispatch(
            getPostsDetails({
              paramsObject,
              id: ele.id,
              pageName,
              type: ele.type,
              title: ele.title,
              sectionId: ele.section_id,
              isLast: ele.id === lastSectionId,
            })
          )
        } else {
          const paramsObject = {
            offset: ele.offset,
            option_for_featured: ele.option_for_featured,
            pagination: ele.pagination,
            items_per_page: ele.items_per_page,
            groups: ele.groups,
            groups_condition: ele.groups_condition,
            categories: ele.categories,
            categories_condition: ele.categories_condition,
            tags: ele.tags,
            tags_condition: ele.tags_condition,
            sorting: ele.sorting,
            rule_type: ele.rule_type,
            type: ele.type,
            disable_latest_record: ele.disable_latest_record,
          }
          if (ele.type === "post-list" || ele.type === "event-list") {
            dispatch(
              getPostsDetails({
                paramsObject,
                pageName,
                id: ele.id,
                type: ele.type,
                defaultView: ele.default_view,
                title: ele.title,
                sectionId: ele.section_id,
                isLast: ele.id === lastSectionId,
              })
            )
          }
        }
      })
    }
    //eslint-disable-next-line
  }, [data?.[pageName]?.sections, pageName])

  const getDatesByDisplayOption = useCallback((deadlinesData) => {
    let data = deadlinesData?.deadlines
      ?.filter((item) => item?.date !== null)
      .filter((item) => {
        if (deadlinesData?.display_option === "all") {
          return item
        } else if (deadlinesData?.display_option === "past") {
          const today = new Date()
          return new Date(item?.date) < today
        } else if (deadlinesData?.display_option === "future") {
          const today = new Date()
          return (
            new Date(item?.date) > today ||
            item?.date === moment(new Date())?.format("YYYY-MM-DD")
          )
        } else if (deadlinesData?.display_option === "current_next") {
          const currentYear = moment().format("YYYY")
          const nextYear = moment().add(1, "year").format("YYYY")

          const today = new Date()
          return (
            (new Date(item?.date) > today &&
              moment(item?.date).format("YYYY") === currentYear) ||
            item?.date === moment(new Date())?.format("YYYY-MM-DD") ||
            moment(item?.date).format("YYYY") === nextYear
          )
        } else {
          return null
        }
      })

    return data
  }, [])

  const getDynamicMinHeight = (type) => {
    if (
      type === "event-carousel" ||
      type === "post-carousel" ||
      type === "course-carousel"
    ) {
      return "260px"
    } else if (type === "deadlines-list") {
      return "70px"
    } else if (type === "Navigations") {
      return "150px"
    } else if (type === "Banner") {
      return "340px"
    } else if (
      type === "NewsLetter" ||
      type === "event-list" ||
      type === "course-list" ||
      type === "post-list"
    ) {
      return "130px"
    } else {
      return "100px"
    }
  }

  return (
    <>
      <Helmet>
        <title>{data?.[pageName]?.seo_tag}</title>
        <meta
          name="description"
          content={data?.[pageName]?.seo_description}
        />
        <meta
          property="title"
          name="title"
          content={data?.[pageName]?.seo_permalink}
        />
        <meta
          property="tag"
          name="tag"
          content={data?.[pageName]?.seo_tag}
        />
      </Helmet>
      {isMainLoading ? (
        <MainLoader />
      ) : sections && sections.length > 0 ? (
        sections.map((ele, index) => {
          const uniqueId = `${ele.section_id}_${ele.id}`
          return (
            <InView
              rootMargin="0px 0px 80px 0px"
              triggerOnce={true}
            >
              {({inView, ref}) => (
                <div
                  ref={ref}
                  key={index}
                  style={{
                    minHeight: getDynamicMinHeight(ele.type),
                  }}
                >
                  {inView &&
                    (ele.type === "event-carousel" ||
                    ele.type === "post-carousel" ||
                    ele.type === "course-carousel" ? (
                      postsData?.[pageName]?.[uniqueId]?.posts?.[1] &&
                      Object.entries(
                        postsData?.[pageName]?.[uniqueId]?.posts?.[1]
                      )?.length > 0 && (
                        <Suspense
                          fallback={
                            <SkeletonLoader
                              index={index}
                              type="carousel"
                              details={ele}
                              sidebars={sidebars}
                            />
                          }
                        >
                          <CarouselListSection
                            details={ele}
                            pageName={pageName}
                            title={ele.title}
                            uniqueId={uniqueId}
                            sidebars={sidebars}
                            type={ele?.type}
                            lastSection={lastSectionId}
                          />
                        </Suspense>
                      )
                    ) : ele.type === "event-scroller" ||
                      ele.type === "post-scroller" ||
                      ele.type === "course-scroller" ? (
                      <Suspense
                        fallback={
                          <SkeletonLoader
                            index={index}
                            type="scroller"
                            sidebars={sidebars}
                          />
                        }
                      >
                        <Scroller
                          id={ele.id}
                          key={index}
                          title={ele.title}
                          type={ele.type}
                          sectionId={ele.section_id}
                          uniqueId={uniqueId}
                          pageName={pageName}
                          sidebars={sidebars || []}
                          is_bought={ele.is_bought}
                          is_registered={ele.is_registered}
                        />
                      </Suspense>
                    ) : ele.type === "deadlines-scroller" ? (
                      <Suspense
                        fallback={
                          <SkeletonLoader
                            index={index}
                            type="scroller"
                            sidebars={sidebars}
                          />
                        }
                      >
                        <DeadlinesScroller
                          id={ele.id}
                          key={index}
                          title={ele.title}
                          type={ele.type}
                          sectionId={ele.section_id}
                          uniqueId={uniqueId}
                          pageName={pageName}
                          details={ele}
                          sidebars={sidebars || []}
                        />
                      </Suspense>
                    ) : ele.type === "deadlines-list" ? (
                      ele?.deadlines?.length > 0 && (
                        <Suspense
                          fallback={
                            <SkeletonLoader
                              index={index}
                              type="deadline-list"
                              details={ele}
                              sidebars={sidebars}
                              length={getDatesByDisplayOption(ele)?.length}
                            />
                          }
                        >
                          <DeadlinesListSection
                            id={ele.id}
                            key={index}
                            title={ele.title}
                            pageName={pageName}
                            details={ele}
                            sidebars={sidebars || []}
                          />
                        </Suspense>
                      )
                    ) : ele.type === "Navigations" ? (
                      <div
                        key={index}
                        className={` mb-4 ${
                          sidebars && Object.entries(sidebars).length === 0
                            ? "container"
                            : ""
                        }`}
                      >
                        {ele?.navigations && ele?.navigations.length > 0 && (
                          <>
                            {!!ele?.display_header ? (
                              sidebars &&
                              Object.entries(sidebars).length === 0 ? (
                                <Title title={ele.title} />
                              ) : (
                                <TitleHeader
                                  title={ele.title}
                                  noMarginTop
                                />
                              )
                            ) : null}
                            <NavigationSection
                              navigations={ele?.navigations}
                              sidebar={
                                !(
                                  sidebars &&
                                  Object.entries(sidebars).length === 0
                                )
                              }
                              index={index}
                            />
                          </>
                        )}
                      </div>
                    ) : ele.type === "Banner" ? (
                      <Suspense
                        fallback={
                          <SkeletonLoader
                            index={index}
                            type="banner"
                            sidebars={sidebars}
                          />
                        }
                      >
                        <Banner
                          key={index}
                          type={ele.type}
                          details={ele}
                          pageName={pageName}
                          sidebars={sidebars}
                        />
                      </Suspense>
                    ) : ele.type === "NewsLetter" ? (
                      sidebars && Object.entries(sidebars).length > 0 ? (
                        <Suspense
                          fallback={
                            <SkeletonLoader
                              index={index}
                              type="newsletter"
                            />
                          }
                        >
                          <Newsletter
                            pageName={pageName}
                            key={index}
                            details={ele}
                            sidebars={true}
                            pageDetails={data}
                            navId={id}
                          />
                        </Suspense>
                      ) : (
                        <Suspense
                          fallback={
                            <SkeletonLoader
                              index={index}
                              type="newsletter"
                            />
                          }
                        >
                          <Newsletter
                            pageName={pageName}
                            key={index}
                            details={ele}
                            sidebars={false}
                            pageDetails={data}
                            navId={id}
                          />
                        </Suspense>
                      )
                    ) : ele.type === "event-list" ? (
                      postsData?.[pageName]?.[uniqueId]?.posts?.[1] &&
                      Object.entries(
                        postsData?.[pageName]?.[uniqueId]?.posts?.[1]
                      ).length > 0 && (
                        <Suspense
                          fallback={
                            <SkeletonLoader
                              index={index}
                              type="list"
                              details={ele}
                              sidebars={sidebars}
                              length={
                                postsData?.[pageName]?.[uniqueId]?.posts?.[1] &&
                                Object.entries(
                                  postsData?.[pageName]?.[uniqueId]?.posts?.[1]
                                )?.length
                              }
                            />
                          }
                        >
                          <EventList
                            key={index}
                            details={ele}
                            title={ele.title}
                            pageName={pageName}
                            uniqueId={uniqueId}
                            sidebars={sidebars}
                            type="event-list"
                          />
                        </Suspense>
                      )
                    ) : ele.type === "post-list" ||
                      ele.type === "course-list" ? (
                      postsData?.[pageName]?.[uniqueId]?.posts?.[1] &&
                      Object.entries(
                        postsData?.[pageName]?.[uniqueId]?.posts?.[1]
                      ).length > 0 && (
                        <Suspense
                          fallback={
                            <SkeletonLoader
                              index={index}
                              type="list"
                              details={ele}
                              sidebars={sidebars}
                              length={
                                postsData?.[pageName]?.[uniqueId]?.posts?.[1] &&
                                Object.entries(
                                  postsData?.[pageName]?.[uniqueId]?.posts?.[1]
                                )?.length
                              }
                            />
                          }
                        >
                          <PostList
                            key={index}
                            details={ele}
                            title={ele.title}
                            pageName={pageName}
                            uniqueId={uniqueId}
                            sidebars={sidebars}
                            type={ele?.type}
                          />
                        </Suspense>
                      )
                    ) : ele?.section_id === 10 ? (
                      <div
                        key={index}
                        className={`mb-4 ${
                          sidebars && Object.entries(sidebars).length === 0
                            ? "container"
                            : ""
                        }`}
                        style={{cursor: "pointer"}}
                        title={memoizedCampaigns?.contentBannerCampaign?.name}
                        onClick={() => {
                          if (
                            memoizedCampaigns?.contentBannerCampaign?.site_url
                          )
                            openLink(
                              memoizedCampaigns?.contentBannerCampaign?.site_url
                            )
                        }}
                      >
                        {memoizedCampaigns?.contentBannerCampaign
                          ?.image_url && (
                          <img
                            src={
                              memoizedCampaigns?.contentBannerCampaign
                                ?.image_url
                            }
                            className="rounded web-kit-width"
                            alt=""
                          />
                        )}
                      </div>
                    ) : null)}
                </div>
              )}
            </InView>
          )
        })
      ) : (
        ""
      )}
    </>
  )
}
export default PageDetails
